import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Rectangle } from '../primitives/rectangle.primitive';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import { Time, ISeriesPrimitive } from 'lightweight-charts';
import { toSortableTime } from './utils/time-normalization.utils';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';
import {
  isOrderBlocksIndicatorUpdate,
  OrderBlocksIndicatorUpdate
} from '../services/indicator-update.service';
import { IndicatorType } from '@shared/types';
import {
  CandlestickUpdatePoint,
  NewCandlestickEvent
} from '../services/candlestick-update.service';

const COLOR_PRESETS = {
  demand: {
    fill: 'rgba(76, 175, 80, 0.2)',
    border: 'rgb(76, 175, 80)'
  },
  supply: {
    fill: 'rgba(248, 76, 76, 0.2)',
    border: 'rgb(248, 76, 76)'
  }
} as const;

type OrderBlocksPrimitiveMap = Map<number, ISeriesPrimitive<Time>[]>;

const keyForOrderBlock = (
  block: OrderBlocksIndicatorUpdate['data'][number]
): string =>
  block.orderBlockStart != null
    ? `s:${Number(block.orderBlockStart)}`
    : `f:${block.time}:${block.top ?? '?'}:${block.bottom ?? '?'}:${block.blockType ?? '?'}`;

export const useOrderBlocks = () => {
  const {
    chartRef,
    candlestickSeriesRef,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();
  const { timeframeIndex, newCandlestick$, getLatestCandlesticks } =
    useChartStreamContext();

  // Keep primitives and merged payload per indicator index so multiple ORDER_BLOCKS configs are isolated.
  const orderBlockRefs = useRef<OrderBlocksPrimitiveMap>(new Map());
  const indicatorDataCacheRef = useRef<
    Map<number, OrderBlocksIndicatorUpdate['data']>
  >(new Map());

  const hasOrderBlocks = useMemo(
    () =>
      indicatorConfigs.overlay.some(
        (indicator) => indicator.type === IndicatorType.ORDER_BLOCKS
      ),
    [indicatorConfigs.overlay]
  );

  const clearPrimitivesForIndicator = useCallback((indicatorIndex: number) => {
    const primitives = orderBlockRefs.current.get(indicatorIndex);
    if (!primitives?.length) return;

    primitives.forEach((primitive) => {
      candlestickSeriesRef.current?.detachPrimitive(primitive);
    });

    orderBlockRefs.current.delete(indicatorIndex);
  }, []);

  const clearAll = useCallback(() => {
    Array.from(orderBlockRefs.current.keys()).forEach(
      clearPrimitivesForIndicator
    );
  }, [clearPrimitivesForIndicator]);

  const createRectanglePrimitive = useCallback(
    (
      item: OrderBlocksIndicatorUpdate['data'][number],
      lastCandleTime: Time
    ): Rectangle | null => {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;

      if (
        !chart ||
        !series ||
        item.bottom === undefined ||
        item.top === undefined ||
        item.orderBlockStart === undefined
      ) {
        return null;
      }

      const colors =
        item.blockType === 'demand'
          ? COLOR_PRESETS.demand
          : COLOR_PRESETS.supply;

      return new Rectangle(
        chart,
        series,
        { time: item.orderBlockStart as Time, price: item.bottom },
        {
          time: (item.orderBlockEnd as Time) || lastCandleTime,
          price: item.top
        },
        {
          fillColor: colors.fill,
          borderColor: colors.border,
          borderWidth: 0.5
        }
      );
    },
    []
  );

  const mergeIndicatorData = useCallback(
    (
      indicatorIndex: number,
      points: OrderBlocksIndicatorUpdate['data'],
      replace: boolean
    ) => {
      // ORDER_BLOCKS deltas are partial; merge by stable block key and keep full local state.
      const nextCache = replace
        ? new Map<string, OrderBlocksIndicatorUpdate['data'][number]>()
        : new Map(
            (indicatorDataCacheRef.current.get(indicatorIndex) ?? []).map(
              (item) => [keyForOrderBlock(item), item]
            )
          );

      points.forEach((point) => {
        nextCache.set(keyForOrderBlock(point), point);
      });

      const merged = Array.from(nextCache.values()).sort(
        (left, right) =>
          toSortableTime(left.orderBlockStart) -
          toSortableTime(right.orderBlockStart)
      );

      indicatorDataCacheRef.current.set(indicatorIndex, merged);
      return merged;
    },
    []
  );

  const drawIndicatorData = useCallback(
    (
      indicatorIndex: number,
      data: OrderBlocksIndicatorUpdate['data'],
      candlestickData: CandlestickUpdatePoint[]
    ) => {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;

      if (!chart || !series || candlestickData.length === 0) return;

      const lastCandleTime = candlestickData[candlestickData.length - 1].time;

      clearPrimitivesForIndicator(indicatorIndex);

      const newPrimitives: ISeriesPrimitive<Time>[] = [];

      data.forEach((item) => {
        const rectangle = createRectanglePrimitive(item, lastCandleTime);
        if (!rectangle) return;

        series.attachPrimitive(rectangle);
        newPrimitives.push(rectangle);
      });

      orderBlockRefs.current.set(indicatorIndex, newPrimitives);
    },
    [clearPrimitivesForIndicator, createRectanglePrimitive]
  );

  const applyUpdate = useCallback(
    (update: OrderBlocksIndicatorUpdate) => {
      const mergedData = mergeIndicatorData(
        update.indicatorIndex,
        update.data,
        update.updateType === 'snapshot'
      );

      const latestCandlesticks = getLatestCandlesticks();
      // Redraw full set because rectangles can be created/updated/closed at arbitrary prior times.
      drawIndicatorData(update.indicatorIndex, mergedData, latestCandlesticks);
    },
    [drawIndicatorData, getLatestCandlesticks, mergeIndicatorData]
  );

  useEffect(() => {
    if (!chartInitialized) return;

    // Open-ended blocks extend with candle boundaries; redraw only on new-candle events.
    const handleNewCandlestick = ({ time }: NewCandlestickEvent) => {
      if (time == null) {
        clearAll();
        return;
      }

      const candlesticks = getLatestCandlesticks();

      if (candlesticks.length === 0) {
        clearAll();
        return;
      }

      // Open-ended blocks use latest candle as end time, so redraw when candles advance.
      indicatorDataCacheRef.current.forEach((data, indicatorIndex) => {
        drawIndicatorData(indicatorIndex, data, candlesticks);
      });
    };

    const subscription = newCandlestick$.subscribe(handleNewCandlestick);

    return () => subscription.unsubscribe();
  }, [
    chartInitialized,
    clearAll,
    drawIndicatorData,
    getLatestCandlesticks,
    newCandlestick$,
    timeframeIndex
  ]);

  useEffect(() => {
    if (hasOrderBlocks) return;

    indicatorDataCacheRef.current.clear();
    clearAll();
  }, [clearAll, hasOrderBlocks]);

  useEffect(() => {
    indicatorDataCacheRef.current.clear();
    clearAll();
  }, [clearAll, timeframeIndex]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasOrderBlocks,
    indicatorType: IndicatorType.ORDER_BLOCKS,
    isExpectedUpdate: isOrderBlocksIndicatorUpdate,
    applyUpdate
  });

  useEffect(() => {
    return () => {
      indicatorDataCacheRef.current.clear();
      clearAll();
    };
  }, [clearAll]);
};
